import { IComponentStyles } from '@fluentui/foundation-legacy';
import { Icon } from '@fluentui/react';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { IPanelProps, Panel } from '@fluentui/react/lib/Panel';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { TrainingJobStore } from 'src/modules/modelTraining/stores/TrainingJobInfoStore';
import { ModelJobStatusInfo } from 'src/modules/modelTraining/types/trainingTypes';
import { jobStatusPrettifier } from 'src/modules/modelTraining/utils/modelJobStatusPrettifier';

const messages = defineMessages({
    hours: { id: 'modelTraining.trainingJobDetailsPanel.hours', defaultMessage: 'hours' },
    close: { id: 'modelTraining.trainingJobDetailsPanel.close', defaultMessage: 'Close' },
    minutes: { id: 'modelTraining.trainingJobDetailsPanel.minutes', defaultMessage: 'minutes' },
    seconds: { id: 'modelTraining.trainingJobDetailsPanel.seconds', defaultMessage: 'seconds' },
    progress: { id: 'modelTraining.trainingJobDetailsPanel.progress', defaultMessage: 'Progress' },
    complete: { id: 'modelTraining.trainingJobDetailsPanel.complete', defaultMessage: 'complete' },
    jobId: { id: 'modelTraining.trainingJobDetailsPanel.jobId', defaultMessage: 'Training job ID' },
    panelClose: { id: 'modelTraining.trainingJobDetailsPanel.panelClose', defaultMessage: 'Close' },
    submitted: { id: 'modelTraining.trainingJobDetailsPanel.submitted', defaultMessage: 'Submitted' },
    totalTime: { id: 'modelTraining.trainingJobDetailsPanel.totalTime', defaultMessage: 'Total time' },
    jobStatus: { id: 'modelTraining.trainingJobDetailsPanel.jobStatus', defaultMessage: 'Job status' },
    outputModel: { id: 'modelTraining.trainingJobDetailsPanel.outputModel', defaultMessage: 'Output model' },
    timeElapsed: { id: 'modelTraining.trainingJobDetailsPanel.timeElapsed', defaultMessage: 'Time elapsed' },
    trainingMode: { id: 'modelTraining.trainingJobDetailsPanel.trainingMode', defaultMessage: 'Training mode' },
    jobHasFailed: { id: 'modelTraining.trainingJobDetailsPanel.jobHasFailed', defaultMessage: 'Job has failed' },
    panelTitle: { id: 'modelTraining.trainingJobDetailsPanel.panelTitle', defaultMessage: 'Training job details' },
    errorDetails: { id: 'modelTraining.trainingJobDetailsPanel.errorDetails', defaultMessage: 'Show error details' },
    jobInProgress: { id: 'modelTraining.trainingJobDetailsPanel.jobInProgress', defaultMessage: 'Job still in progress' },
    trainingProgress: { id: 'modelTraining.trainingJobDetailsPanel.trainingProgress', defaultMessage: 'Training progress' },
    advancedTraining: { id: 'modelTraining.trainingJobDetailsPanel.advancedTraining', defaultMessage: 'Advanced training' },
    evaluationProgress: { id: 'modelTraining.trainingJobDetailsPanel.evaluationProgress', defaultMessage: 'Evaluation progress' },
    standardTraining: { id: 'modelTraining.trainingJobDetailsPanel.standardTraining', defaultMessage: 'Standard training (free)' }
});

export type TrainingJobDetailsPanelProps = IPanelProps & {
    isOpen: boolean;
    onDismiss: () => void;
    trainingJob?: TrainingJobStore;
    onModelClick: (modelId: string) => void;
};

const panelChildrenContentStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { marginTop: '40px' }
});

export const TrainingJobDetailsPanel = observer((props: TrainingJobDetailsPanelProps) => {
    const { localize } = useLocalization();
    const { isOpen = false, onDismiss, trainingJob = new TrainingJobStore(''), onModelClick } = props;

    const [totalElapsedTime, setTotalElapsedTime] = React.useState('');
    const [areErrorDetailsVisible, setAreErrorDetailsVisible] = React.useState(false);

    React.useEffect(() => {
        switch (trainingJob.status) {
            case 'inProgress':
                setTotalElapsedTime(localize(messages.jobInProgress));
                break;
            case 'failed':
                setTotalElapsedTime(localize(messages.jobHasFailed));
                break;
            case 'succeeded':
                setTotalElapsedTime(getStatusTimeFormat(getTotalJobDetails(trainingJob.trainStatus, trainingJob.evaluationStatus)));
                break;
            default:
        }
    }, [
        trainingJob.status,
        trainingJob.trainStatus?.endDateTime,
        trainingJob.trainStatus?.startDateTime,
        trainingJob.evaluationStatus?.endDateTime,
        trainingJob.evaluationStatus?.startDateTime
    ]);

    const getPanelStackElement = (
        title: ReactIntl.FormattedMessage.MessageDescriptor,
        details: string | number,
        extraDetails?: string | number
    ) => (
        <Stack tokens={{ childrenGap: 10 }}>
            <LuisText fontWeight="bold">{localize(title)}</LuisText>
            {extraDetails ? (
                <Stack tokens={{ childrenGap: 5 }}>
                    <LuisText>{details}</LuisText>
                    <LuisText>{extraDetails}</LuisText>
                </Stack>
            ) : (
                <LuisText>{details}</LuisText>
            )}
        </Stack>
    );

    const onRenderFooterContent = React.useCallback(
        () => (
            <div>
                <DefaultButton onClick={onDismiss} text={localize(messages.close)} />
            </div>
        ),
        [onDismiss]
    );

    const getTotalJobDetails = (trainingJobStatus: ModelJobStatusInfo, evaluationJobStatus: ModelJobStatusInfo): ModelJobStatusInfo => {
        if (!evaluationJobStatus?.startDateTime || !evaluationJobStatus?.endDateTime) {
            return trainingJobStatus;
        }

        return {
            status: 'succeeded',
            percentComplete: 100,
            endDateTime:
                new Date(trainingJobStatus.endDateTime) > new Date(evaluationJobStatus.endDateTime)
                    ? trainingJobStatus.endDateTime
                    : evaluationJobStatus.endDateTime,
            startDateTime:
                new Date(trainingJobStatus.startDateTime) < new Date(evaluationJobStatus.startDateTime)
                    ? trainingJobStatus.startDateTime
                    : evaluationJobStatus.startDateTime
        };
    };

    const getStatusTimeFormat = (status: ModelJobStatusInfo) => {
        if (!status?.startDateTime || !status?.endDateTime) {
            return '--';
        }

        const elapsedTimeInMilliSeconds = new Date(status.endDateTime).getTime() - new Date(status.startDateTime).getTime();
        const elapsedSeconds = Math.ceil((elapsedTimeInMilliSeconds / 1000) % 60);
        const elapsedMinutes = Math.floor((elapsedTimeInMilliSeconds / (1000 * 60)) % 60);
        const elapsedHours = Math.floor((elapsedTimeInMilliSeconds / (1000 * 60 * 60)) % 24);

        return `${elapsedHours} ${localize(messages.hours)}, ${elapsedMinutes} ${localize(messages.minutes)}, ${elapsedSeconds} ${localize(
            messages.seconds
        )}`;
    };

    return (
        <Panel
            isLightDismiss
            isOpen={isOpen}
            onDismiss={onDismiss}
            headerText={localize(messages.panelTitle)}
            closeButtonAriaLabel={localize(messages.panelClose)}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom>
            <Stack styles={panelChildrenContentStyles} tokens={{ childrenGap: 20 }}>
                {getPanelStackElement(messages.jobId, trainingJob.id)}

                {getPanelStackElement(messages.submitted, trainingJob.startDateTime ? trainingJob.startDateTime.toLocaleString() : '--')}

                {getPanelStackElement(messages.totalTime, totalElapsedTime)}

                {getPanelStackElement(messages.jobStatus, jobStatusPrettifier(trainingJob.status))}

                {trainingJob.status === 'failed' && (
                    <Stack>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                            <ExternalLink onClick={() => setAreErrorDetailsVisible(!areErrorDetailsVisible)}>
                                {localize(messages.errorDetails)}
                            </ExternalLink>
                            <Icon iconName={areErrorDetailsVisible ? 'ChevronUp' : 'ChevronDown'} />
                        </Stack>
                        {areErrorDetailsVisible && trainingJob.errors.map((e, index) => <li key={index}>{e}</li>)}
                    </Stack>
                )}

                {trainingJob.trainingMode &&
                    getPanelStackElement(
                        messages.trainingMode,
                        localize(trainingJob.trainingMode === 'advanced' ? messages.advancedTraining : messages.standardTraining)
                    )}

                {getPanelStackElement(
                    messages.trainingProgress,
                    `${localize(messages.progress)}: ${trainingJob.trainStatus?.percentComplete ?? '--'}% ${localize(messages.complete)}`,
                    `${localize(messages.timeElapsed)}: ${getStatusTimeFormat(trainingJob.trainStatus)}`
                )}

                {trainingJob.evaluationStatus &&
                    getPanelStackElement(
                        messages.evaluationProgress,
                        `${localize(messages.progress)}: ${trainingJob.evaluationStatus.percentComplete ?? '--'}% ${localize(
                            messages.complete
                        )}`,
                        `${localize(messages.timeElapsed)}:  ${getStatusTimeFormat(trainingJob.evaluationStatus)}`
                    )}

                <Stack tokens={{ childrenGap: 10 }}>
                    <LuisText fontWeight="bold">{localize(messages.outputModel)}</LuisText>
                    <ExternalLink
                        onClick={() => onModelClick(trainingJob.modelId)}
                        disabled={trainingJob.status !== 'succeeded' || trainingJob.evaluationStatus?.percentComplete === 0}>
                        {trainingJob.modelId ?? '--'}
                    </ExternalLink>
                </Stack>
            </Stack>
        </Panel>
    );
});
